'use client'

import { useAuth } from '@/hooks/use-auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { openOAuthPopup } from '@/lib/oauth/popup'
import { toast } from 'sonner'

export type ShopifyIntegrationRecord = {
  id: string
  shop_domain: string
  auth_method: 'legacy' | 'oauth'
  uses_webhooks: boolean
  uses_api: boolean
  webhook_secret?: string | null
  api_access_token_enc?: string | null
  oauth_scopes?: string | null
  custom_app_client_id?: string | null
  custom_app_secret_hint?: string | null
  is_active: boolean
  /** Canonical Shopify billing store for the tenant (at most one). */
  is_billing_store?: boolean
  /** Derived server-side: active + API token + Shop GID, i.e. could actually bill. */
  billing_eligible?: boolean
  disconnected_at?: string | null
  created_at?: string
  updated_at?: string
}

export type ShopifyIntegrationDetails = ShopifyIntegrationRecord & {
  api_access_token?: string | null
}

export type ShopifyIntegrationError = {
  id?: string
  message: string
}

interface UseShopifyIntegrationOptions {
  autoLoad?: boolean
}

const DEFAULT_OPTIONS: Required<UseShopifyIntegrationOptions> = {
  autoLoad: true,
}

export function useShopifyIntegration(options?: UseShopifyIntegrationOptions) {
  const { user, fetchWithAuth } = useAuth()
  const { autoLoad } = { ...DEFAULT_OPTIONS, ...options }

  const [integrations, setIntegrations] = useState<ShopifyIntegrationRecord[]>([])
  const [requiredScopes, setRequiredScopes] = useState<string>('')
  const [billingChannel, setBillingChannel] = useState<string | null>(null)
  const [error, setError] = useState<ShopifyIntegrationError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [syncingCatalogId, setSyncingCatalogId] = useState<string | null>(null)
  const [settingBillingStoreId, setSettingBillingStoreId] = useState<string | null>(null)

  const notifyChanged = useCallback(() => {
    window.dispatchEvent(new Event('shopify-integrations-changed'))
  }, [])

  const refresh = useCallback(async () => {
    if (!user) {
      setIntegrations([])
      return
    }

    setIsLoading(true)
    try {
      const response = await fetchWithAuth('/api/integrations/shopify', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setError({ id: 'load', message: data?.error || 'Failed to fetch Shopify integrations' })
        setIntegrations([])
        return
      }

      const data = await response.json()
      setIntegrations(Array.isArray(data?.integrations) ? (data.integrations as ShopifyIntegrationRecord[]) : [])
      if (data?.requiredScopes) setRequiredScopes(data.requiredScopes as string)
      setBillingChannel(typeof data?.billing_channel === 'string' ? data.billing_channel : null)
    } catch (err) {
      console.error('[useShopifyIntegration] refresh failed', err)
      setError({ id: 'load', message: 'Network error while loading Shopify integrations' })
      setIntegrations([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, user])

  useEffect(() => {
    if (!autoLoad) return
    void refresh()
  }, [autoLoad, refresh])

  // Re-fetch when tab becomes visible (covers OAuth popup close, tab switch, etc.)
  useEffect(() => {
    if (!autoLoad) return
    const handler = () => {
      if (document.visibilityState === 'visible') {
        void refresh()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [autoLoad, refresh])

  const connectIntegration = useCallback(
    async (payload: {
      shop_domain: string
      uses_webhooks: boolean
      uses_api: boolean
      webhook_secret?: string | null
      api_access_token?: string | null
    }) => {
      if (!user) return false

      setIsConnecting(true)
      setError((prev) => (prev?.id === 'form' ? null : prev))

      try {
        const response = await fetchWithAuth('/api/integrations/shopify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError({ id: 'form', message: data?.error || 'Failed to create Shopify integration' })
          return false
        }

        await refresh()
        notifyChanged()
        return true
      } catch (err) {
        console.error('[useShopifyIntegration] connectIntegration failed', err)
        setError({ id: 'form', message: 'Network error while creating Shopify integration' })
        return false
      } finally {
        setIsConnecting(false)
      }
    },
    [fetchWithAuth, notifyChanged, refresh, user],
  )

  const disconnectIntegration = useCallback(
    async (integrationId: string, mode: 'soft' | 'hard' = 'soft') => {
      if (!user) return false

      setError((prev) => (prev?.id === integrationId ? null : prev))
      setDisconnectingId(integrationId)

      try {
        const response = await fetchWithAuth(
          `/api/integrations/shopify/${integrationId}?mode=${mode}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          },
        )

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError({
            id: integrationId,
            message: data?.error || 'Unable to disconnect this Shopify integration.',
          })
          return false
        }

        if (mode === 'hard') {
          setIntegrations((prev) => prev.filter((integration) => integration.id !== integrationId))
        } else {
          setIntegrations((prev) =>
            prev.map((integration) =>
              integration.id === integrationId
                ? { ...integration, is_active: false, uses_api: false, uses_webhooks: false, api_access_token_enc: null, disconnected_at: new Date().toISOString() }
                : integration,
            ),
          )
        }

        notifyChanged()
        return data
      } catch (err) {
        console.error('[useShopifyIntegration] disconnectIntegration failed', err)
        setError({
          id: integrationId,
          message: 'Network error while disconnecting this Shopify integration.',
        })
        return false
      } finally {
        setDisconnectingId(null)
      }
    },
    [fetchWithAuth, notifyChanged, user],
  )

  const toggleIntegration = useCallback(
    async (integrationId: string, nextActive: boolean) => {
      if (!user) return false

      setTogglingId(integrationId)
      setError((prev) => (prev?.id === integrationId ? null : prev))

      try {
        const response = await fetchWithAuth(`/api/integrations/shopify/${integrationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ is_active: nextActive }),
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          setError({ id: integrationId, message: data?.error || 'Failed to update Shopify integration.' })
          return false
        }

        setIntegrations((prev) =>
          prev.map((integration) =>
            integration.id === integrationId ? { ...integration, is_active: nextActive } : integration,
          ),
        )
        notifyChanged()
        return true
      } catch (err) {
        console.error('[useShopifyIntegration] toggleIntegration failed', err)
        setError({ id: integrationId, message: 'Network error while updating integration.' })
        return false
      } finally {
        setTogglingId(null)
      }
    },
    [fetchWithAuth, notifyChanged, user],
  )

  const setBillingStore = useCallback(
    async (integrationId: string) => {
      if (!user) return false

      setSettingBillingStoreId(integrationId)
      setError((prev) => (prev?.id === integrationId ? null : prev))

      try {
        const response = await fetchWithAuth(`/api/integrations/shopify/${integrationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ is_billing_store: true }),
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          setError({ id: integrationId, message: data?.error || 'Failed to set billing store.' })
          return false
        }

        // No contract on the target yet — merchant picks a plan on Shopify's hosted
        // page; the welcome handler completes the switch on return.
        if (data?.redirect_url) {
          window.location.href = data.redirect_url
          return true
        }

        // Server kept the larger incumbent — explain instead of flipping the UI.
        if (data?.switched === false) {
          setError({ id: integrationId, message: data?.message || 'Billing store was not changed.' })
          await refresh()
          return false
        }

        // The switch also moved the subscription/contract — re-fetch, don't flip just the flag.
        await refresh()
        notifyChanged()
        return true
      } catch (err) {
        console.error('[useShopifyIntegration] setBillingStore failed', err)
        setError({ id: integrationId, message: 'Network error while setting billing store.' })
        return false
      } finally {
        setSettingBillingStoreId(null)
      }
    },
    [fetchWithAuth, notifyChanged, refresh, user],
  )

  const updateIntegration = useCallback(
    async (integrationId: string, payload: Record<string, unknown>) => {
      if (!user) return false

      setUpdatingId(integrationId)
      setError((prev) => (prev?.id === integrationId ? null : prev))

      try {
        const response = await fetchWithAuth(`/api/integrations/shopify/${integrationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError({
            id: integrationId,
            message: data?.error || 'Failed to update Shopify integration.',
          })
          return false
        }

        const updated = data?.integration as ShopifyIntegrationRecord | undefined
        if (updated) {
          setIntegrations((prev) => prev.map((integration) => (integration.id === updated.id ? updated : integration)))
        } else {
          await refresh()
        }

        return true
      } catch (err) {
        console.error('[useShopifyIntegration] updateIntegration failed', err)
        setError({
          id: integrationId,
          message: 'Network error while updating Shopify integration.',
        })
        return false
      } finally {
        setUpdatingId(null)
      }
    },
    [fetchWithAuth, refresh, user],
  )

  const syncOrders = useCallback(
    async (integrationId: string, sinceDate: string): Promise<boolean> => {
      if (!user) return false

      setSyncingId(integrationId)
      setError((prev) => (prev?.id === integrationId ? null : prev))

      try {
        const response = await fetchWithAuth(`/api/integrations/shopify/${integrationId}/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sinceDate }),
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          setError({ id: integrationId, message: data?.error || 'Failed to start order sync' })
          return false
        }

        // Keep button disabled briefly to prevent double-clicks while background sync starts
        setTimeout(() => setSyncingId(null), 5000)
        return true
      } catch (err) {
        console.error('[useShopifyIntegration] syncOrders failed', err)
        setError({ id: integrationId, message: 'Network error while starting order sync' })
        setSyncingId(null)
        return false
      }
    },
    [fetchWithAuth, user],
  )

  /**
   * Trigger a Shopify catalog bulk export. Catalog is a full-replace operation
   * — no sinceDate or scope parameter. The route returns 202 for a newly started
   * bulk, 200 when an existing bulk is reused, and 409 / 412 / 503 / 400 / 404
   * for typed Shopify-side or validation failures.
   *
   * Returns `{ ok, status, error? }`. Callers that do not render the hook-level
   * `error` state (e.g. the catalog page, which doesn't surface integration
   * errors in an Alert the way the integration card does) should `toast.error(error)`
   * when `ok` is false.
   */
  const syncCatalog = useCallback(
    async (
      integrationId: string,
    ): Promise<{ ok: boolean; status: number; error?: string }> => {
      if (!user) return { ok: false, status: 0, error: 'Not authenticated' }

      setSyncingCatalogId(integrationId)
      setError((prev) => (prev?.id === integrationId ? null : prev))

      try {
        const response = await fetchWithAuth(
          `/api/integrations/shopify/${integrationId}/catalog-sync`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          },
        )

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          const message = data?.error || 'Failed to start catalog sync'
          setError({ id: integrationId, message })
          setSyncingCatalogId(null)
          return { ok: false, status: response.status, error: message }
        }

        // Bulk export is webhook-driven on the Shopify side — release the lock
        // shortly to allow a manual retry if the operator wants one (mirrors orders).
        setTimeout(() => setSyncingCatalogId(null), 5000)
        return { ok: true, status: response.status }
      } catch (err) {
        console.error('[useShopifyIntegration] syncCatalog failed', err)
        const message = 'Network error while starting catalog sync'
        setError({ id: integrationId, message })
        setSyncingCatalogId(null)
        return { ok: false, status: 0, error: message }
      }
    },
    [fetchWithAuth, user],
  )

  const createCustomAppIntegration = useCallback(
    async (payload: {
      shop_domain: string
      custom_app_client_id: string
      custom_app_client_secret: string
    }): Promise<string | null> => {
      if (!user) return null

      setIsConnecting(true)
      setError((prev) => (prev?.id === 'form' ? null : prev))

      try {
        const response = await fetchWithAuth('/api/integrations/shopify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError({ id: 'form', message: data?.error || 'Failed to save custom app credentials' })
          return null
        }

        await refresh()
        notifyChanged()
        return data?.integration?.id ?? null
      } catch (err) {
        console.error('[useShopifyIntegration] createCustomAppIntegration failed', err)
        setError({ id: 'form', message: 'Network error while saving custom app credentials' })
        return null
      } finally {
        setIsConnecting(false)
      }
    },
    [fetchWithAuth, notifyChanged, refresh, user],
  )

  const startOAuthConnect = useCallback(
    async (shopDomain: string): Promise<void> => {
      if (!user) return

      setIsConnecting(true)
      setError((prev) => (prev?.id === 'form' ? null : prev))

      try {
        const response = await fetchWithAuth(
          `/api/integrations/shopify/oauth/authorize?shop=${encodeURIComponent(shopDomain)}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
          },
        )

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError({ id: 'form', message: data?.error || 'Failed to start Shopify OAuth' })
          return
        }

        const authorizeUrl = data.authorizeUrl as string | undefined
        if (!authorizeUrl) {
          setError({ id: 'form', message: 'No authorize URL returned from server' })
          return
        }

        // Open OAuth flow in popup
        // Note: onClose may fire early due to COOP (popup.closed false positive),
        // but BroadcastChannel stays open so onMessage will fire with real data later
        openOAuthPopup({
          url: authorizeUrl,
          name: 'shopify_oauth',
          onMessage: (result) => {
            if (result.shopify_connected) {
              toast.success(`Shopify store "${result.shopify_connected}" connected successfully`)
            }
            void refresh()
            notifyChanged()
          },
          errorKey: 'shopify_error',
          onError: (err) => {
            toast.error(`Shopify connection failed: ${err}`)
          },
          onClose: () => {
            void refresh()
            notifyChanged()
          },
        })
      } catch (err) {
        console.error('[useShopifyIntegration] startOAuthConnect failed', err)
        setError({ id: 'form', message: 'Network error while starting Shopify connection' })
      } finally {
        setIsConnecting(false)
      }
    },
    [fetchWithAuth, notifyChanged, refresh, user],
  )

  const startCustomAppConnect = useCallback(
    async (payload: {
      shop_domain: string
      custom_app_client_id: string
      custom_app_client_secret: string
    }): Promise<boolean> => {
      // Step 1: save custom app credentials (creates pending integration)
      const integrationId = await createCustomAppIntegration(payload)
      if (!integrationId) return false // error already set

      // Step 2: initiate OAuth flow (reads credentials from saved integration)
      await startOAuthConnect(payload.shop_domain)
      return true
    },
    [createCustomAppIntegration, startOAuthConnect],
  )

  const stats = useMemo(() => {
    const total = integrations.length
    const active = integrations.filter((integration) => integration.is_active).length
    const withWebhooks = integrations.filter((integration) => integration.uses_webhooks).length
    const withApi = integrations.filter((integration) => integration.uses_api).length

    return { total, active, withWebhooks, withApi }
  }, [integrations])

  const activeDomains = useMemo(
    () => integrations.filter((integration) => integration.is_active).map((integration) => integration.shop_domain),
    [integrations],
  )

  const clearError = useCallback((id?: string) => {
    setError((prev) => {
      if (!prev) return null
      if (!id || prev.id === id) return null
      return prev
    })
  }, [])

  const fetchIntegrationDetails = useCallback(
    async (integrationId: string): Promise<ShopifyIntegrationDetails | null> => {
      if (!user) return null
      try {
        const response = await fetchWithAuth(`/api/integrations/shopify/${integrationId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          setError({
            id: integrationId,
            message: data?.error || 'Failed to load integration details.',
          })
          return null
        }

        const data = await response.json().catch(() => ({}))
        return (data?.integration as ShopifyIntegrationDetails | undefined) ?? null
      } catch (err) {
        console.error('[useShopifyIntegration] fetchIntegrationDetails failed', err)
        setError({
          id: integrationId,
          message: 'Network error while loading integration details.',
        })
        return null
      }
    },
    [fetchWithAuth, user],
  )

  return {
    integrations,
    requiredScopes,
    billingChannel,
    stats,
    activeDomains,
    error,
    isLoading,
    isConnecting,
    disconnectingId,
    togglingId,
    updatingId,
    syncingId,
    syncingCatalogId,
    settingBillingStoreId,
    refresh,
    connectIntegration,
    startOAuthConnect,
    startCustomAppConnect,
    disconnectIntegration,
    toggleIntegration,
    setBillingStore,
    updateIntegration,
    syncOrders,
    syncCatalog,
    fetchIntegrationDetails,
    clearError,
  }
}
